/**
 * Value-axis maths for a diverging bar chart (gains right, losses left). Pure and
 * dependency-free so it can be unit-tested in isolation and imported by the chart
 * without pulling in any React/Recharts code.
 */

export interface DivergingAxis {
  /** [min, max] value-axis bounds, snapped to whole tick steps. */
  domain: [number, number]
  /** Evenly-spaced, round tick values spanning the domain (always includes 0). */
  ticks: number[]
}

// Geometric midpoints between the 1·2·5·10 "nice" steps. Rounding `normalized`
// to the nearest of these (rather than always up) lands the step close to the
// rough estimate, so a tight range gets an intermediate gridline instead of one
// coarse jump.
const NICE_THRESHOLD_LOW = Math.SQRT2 // √(1·2)
const NICE_THRESHOLD_MID = Math.sqrt(10) // √(2·5)
const NICE_THRESHOLD_HIGH = Math.sqrt(50) // √(5·10)

/** Round a rough step to the nearest clean 1/2/5×10ⁿ value, never below
 * `minStep` (1 by default, since follower counts are whole numbers). */
function niceStep(rough: number, minStep: number): number {
  const safe = Math.max(rough, Number.EPSILON)
  const magnitude = 10 ** Math.floor(Math.log10(safe))
  const normalized = safe / magnitude
  const niceNormalized =
    normalized < NICE_THRESHOLD_LOW
      ? 1
      : normalized < NICE_THRESHOLD_MID
        ? 2
        : normalized < NICE_THRESHOLD_HIGH
          ? 5
          : 10
  return Math.max(minStep, niceNormalized * magnitude)
}

/**
 * Even, round ticks for a diverging value axis (gains right, losses left). The
 * step is a clean 1/2/5×10ⁿ number so every interval is identical and labels
 * read cleanly; 0 is always a tick (the baseline) and the domain is snapped to
 * whole steps so the axis never ends on a ragged fractional value. The range
 * always extends at least one full step on **both** sides of zero — even for
 * one-directional data — so the axis always signals the chart can diverge both
 * ways (gains right, losses left).
 */
export function buildDivergingAxis(
  values: number[],
  targetTicks = 5,
  minStep = 1,
): DivergingAxis {
  // At least two ticks, or the `(safeTargetTicks - 1)` divisor below is 0/negative.
  const safeTargetTicks = Math.max(2, targetTicks)
  const min = Math.min(0, ...values)
  const max = Math.max(0, ...values)
  const step = niceStep((max - min || minStep) / (safeTargetTicks - 1), minStep)

  // Snap to whole steps, and always keep at least one step on each side of zero
  // (so all-gain, all-loss, and all-flat data still show a both-directions axis).
  const minTick = Math.min(-step, Math.floor(min / step) * step)
  const maxTick = Math.max(step, Math.ceil(max / step) * step)

  const count = Math.round((maxTick - minTick) / step) + 1
  const ticks = Array.from({ length: count }, (_, i) => {
    const value = minTick + i * step
    return value === 0 ? 0 : value // normalise -0
  })

  return { domain: [minTick, maxTick], ticks }
}
